import { useEffect, useMemo, useState } from "react";
import PageLayout from "@/components/feature/PageLayout";
import { useSupabaseData } from "@/hooks/useSupabaseData";
import { fallbackPracticeData, type PracticeSession, type SpecialSession } from "@/mocks/practices";
import { getPracticeData, liveNotices, liveSpecials } from "@/services/practices";

// The schedule is edited in Connect (Admin → Website). Triumph is boys-only, so
// sessions only filter by grade. If Supabase can't be reached the page shows
// src/mocks/practices.ts instead.

const DAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const gradeOptions = [
  { value: "all", label: "All grades" },
  { value: "0", label: "K" },
  { value: "1", label: "1st" },
  { value: "2", label: "2nd" },
  { value: "3", label: "3rd" },
  { value: "4", label: "4th" },
  { value: "5", label: "5th" },
  { value: "6", label: "6th" },
  { value: "7", label: "7th" },
  { value: "8", label: "8th" },
  { value: "9", label: "9th" },
  { value: "10", label: "10th" },
  { value: "11", label: "11th" },
  { value: "12", label: "12th" },
];

const STORAGE_KEY = "triumph:practice-filters";

function toneClass(s: PracticeSession): string {
  return s.paid ? "text-[#4ADE80]" : "text-[#FFFFFF]";
}

/** "17:30" → { time: "5:30", suffix: "PM" } */
function clock(t: string) {
  const [h, m] = t.split(":").map(Number);
  return { time: `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, "0")}`, suffix: h >= 12 ? "PM" : "AM" };
}

/** "5:30–6:30 PM", or "11:30 AM–12:30 PM" across noon. */
function timeRange(start24: string, end24: string): string {
  const a = clock(start24);
  const b = clock(end24);
  return a.suffix === b.suffix ? `${a.time}–${b.time} ${b.suffix}` : `${a.time} ${a.suffix}–${b.time} ${b.suffix}`;
}

/** "2026-11-02" read as a calendar day (no timezone shift). */
function asDate(d: string): Date {
  const [y, m, day] = d.split("-").map(Number);
  return new Date(y, m - 1, day);
}

const fmt = (d: string, opts: Intl.DateTimeFormatOptions) => asDate(d).toLocaleDateString("en-US", opts);

/** One tryout/clinic block: its days, date range and locations. */
function groupSpecials(specials: SpecialSession[]) {
  const titles = Array.from(new Set(specials.map((s) => s.blockTitle)));
  return titles.map((title) => {
    const rows = specials.filter((s) => s.blockTitle === title);
    const dates = Array.from(new Set(rows.map((r) => r.date))).sort();
    const places = Array.from(new Set(rows.map((r) => r.location).filter(Boolean)));
    const first = dates[0];
    const last = dates[dates.length - 1];
    const long: Intl.DateTimeFormatOptions = { weekday: "long", month: "long", day: "numeric" };
    const range = first === last ? fmt(first, long) : `${fmt(first, long)} – ${fmt(last, long)}`;
    return {
      title,
      subtitle: [range, places.join(", ")].filter(Boolean).join(" · "),
      days: dates.map((d) => ({
        day: `${fmt(d, { weekday: "short" })} · ${fmt(d, { month: "short", day: "numeric" })}`,
        slots: rows
          .filter((r) => r.date === d)
          .sort((a, b) => a.start24.localeCompare(b.start24))
          .map((r) => ({ time: timeRange(r.start24, r.end24), group: r.group })),
      })),
    };
  });
}

export default function Practices() {
  const { data } = useSupabaseData(getPracticeData, fallbackPracticeData);
  const [grade, setGrade] = useState<string>("all");

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw) as { grade?: string };
      if (typeof saved.grade === "string") setGrade(saved.grade);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ grade }));
    } catch {
      /* ignore */
    }
  }, [grade]);

  const reset = () => setGrade("all");

  const specialBlocks = useMemo(() => groupSpecials(liveSpecials(data.specials)), [data.specials]);
  const notices = useMemo(() => liveNotices(data.notices), [data.notices]);

  // "Monday–Thursday practices begin Monday, November 2, 2026."
  const effectiveLine = useMemo(() => {
    const dayNames = DAY_ORDER.filter((d) => data.sessions.some((s) => s.day === d));
    const starts = data.sessions.map((s) => s.startsOn).filter((d): d is string => !!d).sort();
    if (dayNames.length === 0 || starts.length === 0) return null;
    const span = dayNames.length === 1 ? dayNames[0] : `${dayNames[0]}–${dayNames[dayNames.length - 1]}`;
    return `${span} practices begin ${fmt(starts[0], { weekday: "long", month: "long", day: "numeric", year: "numeric" })}.`;
  }, [data.sessions]);

  const days = useMemo(() => {
    const gradeNum = grade === "all" ? null : parseInt(grade, 10);
    const visible = data.sessions.filter((s) => {
      if (s.universal || gradeNum === null) return true;
      return (s.gradeMin ?? 0) <= gradeNum && gradeNum <= (s.gradeMax ?? 12);
    });
    return DAY_ORDER.map((name) => {
      const daySessions = visible.filter((s) => s.day === name).sort((a, b) => a.start24.localeCompare(b.start24));
      const firstStart = data.sessions
        .filter((s) => s.day === name && s.startsOn)
        .map((s) => s.startsOn as string)
        .sort()[0];
      return {
        name,
        starts: firstStart ? `Starts ${fmt(firstStart, { month: "short", day: "numeric" })}` : "",
        sessions: daySessions,
      };
    }).filter((d) => d.sessions.length > 0);
  }, [data.sessions, grade]);

  // Locations follow the grade filter: only places used by the sessions shown (plus live tryouts).
  const shownLocations = useMemo(() => {
    const used = new Set([
      ...days.flatMap((d) => d.sessions.map((s) => s.location)),
      ...liveSpecials(data.specials).map((s) => s.location),
    ]);
    return data.locations.filter((l) => used.has(l.acronym));
  }, [days, data.specials, data.locations]);

  const hasPaid = data.sessions.some((s) => s.paid);

  return (
    <PageLayout>
      {/* Header */}
      <section className="w-full px-4 md:px-8 lg:px-12 pt-28 md:pt-32 pb-8 bg-onyx-50">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-0.5 bg-[#F2C511]"></div>
            <span className="font-body text-[#F2C511] text-xs font-semibold tracking-[0.2em] uppercase">
              Weekly Calendar
            </span>
          </div>
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-white uppercase tracking-tight">
            Practice Schedule
          </h1>
          <p className="font-body text-white/50 text-sm md:text-base mt-3">{data.season} Season</p>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 lg:px-12 pb-16 md:pb-24 bg-onyx-50">
        <div className="max-w-5xl mx-auto">
          {/* Tryouts / clinics */}
          {specialBlocks.map((block) => (
            <div key={block.title} className="border border-[#F2C511] bg-[#F2C511]/5 p-5 md:p-7 mb-6">
              <h2 className="font-heading text-[#F2C511] text-sm font-bold uppercase tracking-[0.2em] mb-1">
                {block.title}
              </h2>
              <p className="font-body text-white/55 text-xs md:text-sm mb-5">{block.subtitle}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {block.days.map((d) => (
                  <div key={d.day}>
                    <p className="font-heading text-white text-sm font-bold uppercase tracking-wider mb-2">
                      {d.day}
                    </p>
                    <ul className="space-y-1.5">
                      {d.slots.map((s) => (
                        <li key={s.time + s.group} className="font-body text-xs md:text-sm leading-snug">
                          <span className="text-[#F2C511] tabular-nums">{s.time}</span>
                          <span className="text-white/70"> · {s.group}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Information banner */}
          <div className="border border-[#F2C511]/40 bg-[#F2C511]/5 p-5 md:p-7">
            <h2 className="font-heading text-[#F2C511] text-sm font-bold uppercase tracking-[0.2em] mb-4">
              Weekly Practice — Effective Dates
            </h2>
            {effectiveLine ? (
              <ul className="space-y-2 mb-5">
                <li className="flex items-start gap-3">
                  <i className="ri-calendar-check-line text-[#F2C511] text-base mt-0.5"></i>
                  <span className="font-body text-white text-sm md:text-base">{effectiveLine}</span>
                </li>
              </ul>
            ) : null}
            <p className="font-body text-white/55 text-xs md:text-sm leading-relaxed border-t border-[#F2C511]/20 pt-4">
              This is the base weekly schedule for the {data.season} season. Adjustments for specific weeks — due to
              holidays, facility availability, or special events — will be communicated on a case-by-case
              basis via email.
            </p>
          </div>

          {/* Notices from Connect (e.g. "We will fill out rosters until Nov 15th") */}
          {notices.map((n, i) => (
            <div key={i} className="mt-6 border border-[#F2C511] bg-[#F2C511]/5 p-5 md:p-7">
              {n.title ? (
                <h2 className="font-heading text-[#F2C511] text-sm font-bold uppercase tracking-[0.2em] mb-2">
                  {n.title}
                </h2>
              ) : null}
              <p className="font-body text-white text-sm md:text-base leading-relaxed whitespace-pre-line">{n.message}</p>
            </div>
          ))}

          {/* Filters */}
          <div className="mt-6 border border-onyx-400 bg-gunmetal-50/40 p-4 md:p-5 flex flex-col md:flex-row md:items-end gap-4 md:gap-6">
            <div>
              <label
                htmlFor="grade-filter"
                className="block font-body text-white/50 text-[11px] uppercase tracking-[0.2em] mb-2"
              >
                Grade
              </label>
              <select
                id="grade-filter"
                value={grade}
                onChange={(e) => setGrade(e.target.value)}
                className="font-body text-sm bg-onyx-50 border border-onyx-400 text-white px-3 py-2 focus:outline-none focus:border-[#F2C511] w-full md:w-44"
              >
                {gradeOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:ml-auto">
              <button
                type="button"
                onClick={reset}
                className="font-body text-xs uppercase tracking-wider px-4 py-2 border border-onyx-400 text-white/60 hover:text-white hover:border-white/40 transition-colors"
              >
                Reset
              </button>
            </div>
          </div>

          {/* Legend */}
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2">
            <span className="inline-flex items-center gap-2 font-body text-xs uppercase tracking-wider">
              <span className="w-2.5 h-2.5 bg-[#FFFFFF]"></span>
              <span className="text-white/70">Boys</span>
            </span>
            {hasPaid ? (
              <span className="inline-flex items-center gap-2 font-body text-xs uppercase tracking-wider">
                <span className="w-2.5 h-2.5 bg-[#4ADE80]"></span>
                <span className="text-white/70">Open Skills — Pay Per Session</span>
              </span>
            ) : null}
          </div>

          {/* Days */}
          {days.length === 0 ? (
            <p className="mt-8 font-body text-white/50 text-sm">
              No sessions match the current filters. Try adjusting the grade selection.
            </p>
          ) : (
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
              {days.map((day) => (
                <section key={day.name} className="border border-onyx-400 bg-gunmetal-50/40">
                  <div className="flex items-baseline justify-between gap-3 px-5 py-4 border-b border-onyx-400">
                    <h2 className="font-heading text-[#F2C511] text-lg md:text-xl font-bold uppercase tracking-wider">
                      {day.name}
                    </h2>
                    <span className="font-body text-white/40 text-[11px] uppercase tracking-wider">
                      {day.starts}
                    </span>
                  </div>
                  <ul className="divide-y divide-onyx-400/60">
                    {day.sessions.map((s, i) => (
                      <li key={i} className="px-5 py-3 flex flex-col sm:flex-row sm:items-center sm:gap-4">
                        <span className="font-body text-white text-xs sm:text-sm tabular-nums sm:w-32 shrink-0">
                          {timeRange(s.start24, s.end24)}
                        </span>
                        <span className="flex-1 min-w-0 mt-1 sm:mt-0">
                          <span className={`font-body text-sm font-medium ${toneClass(s)}`}>{s.label}</span>
                          {s.paid && (
                            <span className="ml-2 inline-block bg-[#173404] text-[#4ADE80] text-[10px] font-semibold tracking-wider uppercase px-2 py-0.5 align-middle">
                              Pay Per Session
                            </span>
                          )}
                          {s.universal && !s.paid && (
                            <span className="ml-2 inline-block border border-white/20 text-white/60 text-[10px] font-semibold tracking-wider uppercase px-2 py-0.5 align-middle">
                              Open to all
                            </span>
                          )}
                        </span>
                        <a
                          href="#locations"
                          className="font-body text-white/75 hover:text-white text-xs uppercase tracking-wider mt-1 sm:mt-0 shrink-0 underline underline-offset-4 decoration-white/30"
                        >
                          {s.location}
                        </a>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          )}

          {/* Locations */}
          {shownLocations.length > 0 ? (
          <div id="locations" className="mt-12 scroll-mt-28">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-0.5 bg-[#F2C511]"></div>
              <h2 className="font-heading text-white text-lg md:text-2xl font-bold uppercase tracking-tight">
                Locations
              </h2>
            </div>
            <ul className="border border-onyx-400 divide-y divide-onyx-400">
              {shownLocations.map((loc) => (
                <li
                  key={loc.acronym}
                  className="px-5 py-4 flex flex-col md:flex-row md:items-center md:gap-4 bg-gunmetal-50/30"
                >
                  <span className="font-heading text-[#F2C511] text-sm font-bold uppercase tracking-wider md:w-40 shrink-0">
                    {loc.acronym}
                  </span>
                  <span className="font-body text-white text-sm flex-1 mt-1 md:mt-0">{loc.name}</span>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-body text-white/45 hover:text-white text-xs md:text-sm mt-1 md:mt-0 md:text-right inline-flex items-center gap-1.5"
                  >
                    <i className="ri-map-pin-line"></i>
                    {loc.address}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          ) : null}
        </div>
      </section>
    </PageLayout>
  );
}
